import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_BATCH_SIZE = 20;
const DEFAULT_TRIGGER_OFFSET = 120;

function getRowIdentifier(row, index) {
  return (
    row?.rowKey ??
    row?.id ??
    row?.Id ??
    row?.leadId ??
    row?.LeadId ??
    row?.accountId ??
    row?.AccountId ??
    `${JSON.stringify(row)}-${index}`
  );
}

function dedupeRows(rows) {
  const seen = new Set();

  return rows.filter((row, index) => {
    const rowIdentifier = getRowIdentifier(row, index);
    if (seen.has(rowIdentifier)) {
      return false;
    }

    seen.add(rowIdentifier);
    return true;
  });
}

export default function useLazyCursorPagination(
  fetchPage,
  batchSize = DEFAULT_BATCH_SIZE,
  triggerOffset = DEFAULT_TRIGGER_OFFSET,
) {
  const containerRef = useRef(null);
  const nextCursorRef = useRef(null);
  const hasMoreRef = useRef(true);
  const isLoadingRef = useRef(false);
  const isMountedRef = useRef(false);
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const loadMore = useCallback(async () => {
    if (isLoadingRef.current) return;
    if (!hasMoreRef.current) return;
    if (typeof fetchPage !== "function") return;

    isLoadingRef.current = true;
    if (isMountedRef.current) {
      setIsLoading(true);
    }

    try {
      const currentCursor = nextCursorRef.current;
      const response = await fetchPage({
        limit: batchSize,
        cursor: currentCursor,
      });

      const nextRows = Array.isArray(response?.data) ? response.data : [];
      const nextCursor = response?.nextCursor ?? null;
      const isInitialRequest = currentCursor === null;
      const isCursorStalled =
        currentCursor !== null && nextCursor === currentCursor;

      setRows((currentRows) =>
        dedupeRows(
          isInitialRequest ? nextRows : [...currentRows, ...nextRows],
        ),
      );

      nextCursorRef.current = isCursorStalled ? null : nextCursor;
      hasMoreRef.current = Boolean(nextCursor) && !isCursorStalled;
      if (isMountedRef.current) {
        setHasMore(hasMoreRef.current);
        setHasLoadedOnce(true);
      }
    } catch (error) {
      console.error("Failed to load cursor page:", error);
      hasMoreRef.current = false;
      if (isMountedRef.current) {
        setHasMore(false);
        setHasLoadedOnce(true);
      }
      if (nextCursorRef.current === null) {
        setRows([]);
      }
    } finally {
      isLoadingRef.current = false;
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [batchSize, fetchPage]);

  useEffect(() => {
    isMountedRef.current = true;
    nextCursorRef.current = null;
    hasMoreRef.current = true;
    isLoadingRef.current = false;
    setRows([]);
    setHasMore(true);
    setIsLoading(false);
    setHasLoadedOnce(false);
    void loadMore();

    return () => {
      isMountedRef.current = false;
    };
  }, [loadMore]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let frameId = null;
    const handleScroll = () => {
      if (frameId !== null) return;

      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        const remainingScroll =
          container.scrollHeight - container.scrollTop - container.clientHeight;

        if (remainingScroll <= triggerOffset) {
          void loadMore();
        }
      });
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      container.removeEventListener("scroll", handleScroll);
    };
  }, [loadMore, triggerOffset]);

  return {
    rows,
    loadMore,
    hasMore,
    isLoading,
    hasLoadedOnce,
    containerRef,
  };
}
